import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ResponsiveContainer, ComposedChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  Activity, RefreshCw, Users, LogIn, MoonStar, Timer,
  ArrowUp, ArrowDown, ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { fetchActivitySummary } from '@/lib/api/userActivity';
import {
  ACTIVITY_WINDOWS, activityTotals, isOnlineNow, isDormant, formatActiveTime,
  formatAgo, describeDevice, displayName,
  type ActivityWindow, type ActivitySummary, type UserActivityRow,
} from '@/lib/activity';
import { cn } from '@/lib/utils';

// ── User Activity ───────────────────────────────────────────────────────────
//
// The people-shaped view of the same history /logs shows as a flat feed, plus
// the half that page never had: who signs in, when, and who has stopped.
//
// Every number here comes from /api/activity in one call. The rollups are
// computed in the database because audit_log is far too large to count in the
// browser - /logs caps at 2000 rows for that reason, which is fine for a feed
// and wrong for a total.

const WINDOW_LABELS: Record<ActivityWindow, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
  365: 'Last year',
};

/** Accounts unused for this long are called out. */
const DORMANT_AFTER_DAYS = 30;

const AXIS_STYLE = { fontSize: 11, fill: 'var(--ink-muted)' } as const;

type SortKey = 'name' | 'login_count' | 'last_seen_at' | 'active_seconds' | 'action_count';

/** Columns sort biggest-first on the first click, except the name. */
const DESCENDING_FIRST: Record<SortKey, boolean> = {
  name: false,
  login_count: true,
  last_seen_at: true,
  active_seconds: true,
  action_count: true,
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-violet-100 text-violet-700',
  admin:       'bg-blue-100 text-blue-700',
  manager:     'bg-teal-100 text-teal-700',
  management:  'bg-teal-100 text-teal-700',
  technician:  'bg-amber-100 text-amber-700',
  marketing:   'bg-pink-100 text-pink-700',
  retail:      'bg-slate-100 text-slate-700',
  retail_shop: 'bg-slate-100 text-slate-700',
};

function roleColor(role: string | null) {
  return (role && ROLE_COLORS[role]) || 'bg-gray-100 text-gray-700';
}

function formatRole(role: string | null) {
  if (!role) return '—';
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** A day label for the chart axis, e.g. "24 Sep". */
function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function StatTile({
  icon: Icon, label, value, hint, tone,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
  tone?: 'warn';
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            'rounded-md p-2',
            tone === 'warn' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground',
          )}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-2xl font-semibold tabular-nums leading-tight">{value}</div>
            <div className="text-sm text-muted-foreground">{label}</div>
            {hint && <div className="text-xs text-muted-foreground/80 mt-0.5">{hint}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Green when the session is live, hollow otherwise. */
function PresenceDot({ online }: { online: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block h-2 w-2 rounded-full shrink-0',
        online ? 'bg-emerald-500' : 'border border-muted-foreground/40',
      )}
    />
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-sm">
      <div className="font-medium mb-1">{dayLabel(label)}</div>
      <div className="space-y-0.5 tabular-nums">
        <div><span className="text-muted-foreground">Sign-ins: </span>{point.login_count}</div>
        <div><span className="text-muted-foreground">People: </span>{point.active_users}</div>
        <div><span className="text-muted-foreground">Actions: </span>{point.action_count}</div>
      </div>
    </div>
  );
}

function SortHeader({
  column, label, sort, onSort, align = 'left',
}: {
  column: SortKey;
  label: string;
  sort: { key: SortKey; desc: boolean };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === column;
  return (
    <th className={cn('px-3 py-2 font-medium', align === 'right' && 'text-right')}>
      <button
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground transition-colors',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        {active && (sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function PeopleTable({ summary }: { summary: ActivitySummary }) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: 'last_seen_at', desc: true,
  });

  const toggleSort = (key: SortKey) => {
    setSort(current => current.key === key
      ? { key, desc: !current.desc }
      : { key, desc: DESCENDING_FIRST[key] });
  };

  const rows = useMemo(() => {
    const value = (row: UserActivityRow): string | number => {
      switch (sort.key) {
        case 'name': return displayName(row).toLowerCase();
        // Never-seen sorts as the beginning of time rather than as now, so the
        // accounts nobody has touched land together at one end of the column
        // instead of scattering through the middle.
        case 'last_seen_at': return row.last_seen_at ? new Date(row.last_seen_at).getTime() : 0;
        default: return row[sort.key] ?? 0;
      }
    };

    return [...summary.users].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      const order = left < right ? -1 : left > right ? 1 : 0;
      return sort.desc ? -order : order;
    });
  }, [summary.users, sort]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">People</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs border-b">
              <tr>
                <SortHeader column="name" label="User" sort={sort} onSort={toggleSort} />
                <th className="px-3 py-2 font-medium text-muted-foreground">Role</th>
                <SortHeader column="login_count" label="Sign-ins" sort={sort} onSort={toggleSort} align="right" />
                <SortHeader column="last_seen_at" label="Last seen" sort={sort} onSort={toggleSort} align="right" />
                <SortHeader column="active_seconds" label="Time in app" sort={sort} onSort={toggleSort} align="right" />
                <SortHeader column="action_count" label="Actions" sort={sort} onSort={toggleSort} align="right" />
                <th className="px-3 py-2 font-medium text-muted-foreground">Last action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const online = isOnlineNow(row.last_seen_at, summary.now, summary.onlineWindowSeconds);
                const dormant = isDormant(row, summary.now, DORMANT_AFTER_DAYS);

                return (
                  <tr key={row.technician_id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <PresenceDot online={online} />
                        <div className="min-w-0">
                          <div className={cn('truncate', !row.is_active && 'text-muted-foreground line-through')}>
                            {displayName(row)}
                          </div>
                          {row.email && row.name && (
                            <div className="text-xs text-muted-foreground truncate">{row.email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className={cn('font-normal', roleColor(row.role))}>
                        {formatRole(row.role)}
                      </Badge>
                      {!row.is_active && (
                        <Badge variant="outline" className="ml-1 font-normal">Deactivated</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.login_count || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {online
                        ? <span className="text-emerald-600 font-medium">Now</span>
                        : <span className={cn(dormant && 'text-amber-600')}>{formatAgo(row.last_seen_at, summary.now)}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {formatActiveTime(row.active_seconds)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.action_count || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      {row.last_action_at ? formatAgo(row.last_action_at, summary.now) : '—'}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    No accounts are linked to a login yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentSignIns({ summary }: { summary: ActivitySummary }) {
  // The table is keyed by technician; sessions carry only ids, so resolve the
  // name once here rather than searching the list per row.
  const names = useMemo(() => {
    const byUser = new Map<string, string>();
    summary.users.forEach(u => byUser.set(u.user_id, displayName(u)));
    return byUser;
  }, [summary.users]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent sign-ins</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <div className="max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody>
              {summary.recentSessions.map(session => (
                <tr key={session.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatAgo(session.started_at, summary.now)}
                  </td>
                  <td className="px-3 py-2 truncate">
                    {names.get(session.user_id) ?? session.user_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {describeDevice(session.user_agent)}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatActiveTime(session.active_seconds)}
                  </td>
                </tr>
              ))}
              {!summary.recentSessions.length && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                    No sign-ins recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export function UserActivityPage() {
  const [days, setDays] = useState<ActivityWindow>(30);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['user-activity', days],
    queryFn: () => fetchActivitySummary(days),
    // Keep the previous window on screen while the next one loads, so changing
    // the range moves the numbers rather than blanking the page.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  const totals = useMemo(
    () => (data ? activityTotals(data, DORMANT_AFTER_DAYS) : null),
    [data],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Activity className="h-6 w-6 text-muted-foreground" />
        <h1 className="text-3xl font-bold">User Activity</h1>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value) as ActivityWindow)}
            className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            {ACTIVITY_WINDOWS.map(w => (
              <option key={w} value={w}>{WINDOW_LABELS[w]}</option>
            ))}
          </select>
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40">
          <CardContent className="pt-5 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
            <div className="text-sm">
              <div className="font-medium">Could not load activity</div>
              <div className="text-muted-foreground">{(error as Error).message}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && !data && (
        <div className="py-16 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {data && totals && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              icon={Users}
              label="Active now"
              value={String(totals.activeNow)}
              hint={`Seen in the last ${Math.round(data.onlineWindowSeconds / 60)} min`}
            />
            <StatTile
              icon={LogIn}
              label="Signed in today"
              value={String(totals.signedInToday)}
              hint={`${data.users.length} accounts with a login`}
            />
            <StatTile
              icon={Timer}
              label="Time in app"
              value={formatActiveTime(totals.totalActiveSeconds)}
              hint={`${totals.totalActions.toLocaleString()} audited actions`}
            />
            <StatTile
              icon={MoonStar}
              label="Dormant accounts"
              value={String(totals.dormant)}
              hint={totals.neverSignedIn
                ? `${totals.neverSignedIn} never signed in`
                : `Unused for ${DORMANT_AFTER_DAYS}+ days`}
              tone={totals.dormant ? 'warn' : undefined}
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Sign-ins per day
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {WINDOW_LABELS[data.days as ActivityWindow]}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={data.daily} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke="var(--grid-line)" strokeWidth={1} vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={AXIS_STYLE}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--axis-line)' }}
                    tickFormatter={dayLabel}
                    minTickGap={24}
                  />
                  <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--grid-line)', fillOpacity: 0.4 }} />
                  {/* Bars are the count of sign-ins; the line is how many
                      distinct people they came from. The two diverge exactly
                      when a few people are signing in repeatedly, which is the
                      thing worth seeing. */}
                  <Bar dataKey="login_count" fill="var(--series-1)" radius={[2, 2, 0, 0]} maxBarSize={22} />
                  <Line
                    type="monotone"
                    dataKey="active_users"
                    stroke="var(--series-2)"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-3 rounded-sm" style={{ background: 'var(--series-1)' }} /> Sign-ins
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-0.5 w-3 rounded-sm" style={{ background: 'var(--series-2)' }} /> Distinct people
                </span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <PeopleTable summary={data} />
            </div>
            <RecentSignIns summary={data} />
          </div>

          <p className="text-xs text-muted-foreground">
            Presence is recorded once a minute while a tab is in the foreground, so time in
            app counts use rather than open windows. Sign-ins before this page was deployed
            were never recorded and do not appear.
          </p>
        </>
      )}
    </div>
  );
}
